import { ARIA, EVENTS, MISC, SELECTORS } from '@bs/constants';

/**
 * The Accordion module which handles accordion items expansion and collapse on click.
 * @class Accordion
 */
export default class Accordion {
  /**
   * Accordion constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    // Store original tabindex values for restoration
    this.originalTabIndexes = new Map();

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} APIResults A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.accordionButton = this.element.querySelector(
      SELECTORS.ACCORDION_BUTTON
    );
    this.accordionPanel = this.element.querySelector(SELECTORS.ACCORDION_PANEL);
    this.onlyExpand = this.element.dataset.onlyExpand;

    if (this.accordionPanel.getAttribute(ARIA.HIDDEN) === MISC.TRUE) {
      this.accordionPanel.style.height = '0px';
      this.disablePanelFocus();
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onClick = this.onClick.bind(this);
    this.onExternalEvent = this.onClick.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Accordion A reference to the instance of the class
   * @chainable
   */
  enable() {
    this.accordionButton.addEventListener(EVENTS.CLICK, this.onClick);
    window.addEventListener(EVENTS.COLLAPSE_ACCORDION, this.onExternalEvent);

    return this;
  }

  /**
   * The external event handler collapses/expands the accordion if occured within it.
   * @param {Event} event - The event object
   * @returns {object} A reference to the current instance of the class
   */
  onExternalEvent(event) {
    if (this.element.contains(event.target)) {
      this.onClick();
    }

    return this;
  }

  /**
   * The click event handler for the accordion button. Expands or collapses the accordion panel.
   * @returns {object} A reference to the current instance of the class
   */
  onClick() {
    const expanded =
      this.accordionButton.getAttribute(ARIA.EXPANDED) === MISC.TRUE;
    this.accordionButton.setAttribute(ARIA.EXPANDED, !expanded);
    this.accordionPanel.setAttribute(ARIA.HIDDEN, expanded);

    if (!expanded) {
      this.constructor.expandSection(
        this.accordionPanel,
        this.accordionButton,
        this.onlyExpand
      );
      this.enablePanelFocus();
    } else if (this.onlyExpand !== MISC.TRUE) {
      this.constructor.collapseSection(this.accordionPanel);
      this.disablePanelFocus();
    }

    return this;
  }

  /**
   * Disables focus on all focusable elements inside the accordion panel
   * to prevent tab navigation into closed accordions (WCAG compliance)
   * @returns {object} A reference to the current instance of the class
   */
  disablePanelFocus() {
    const focusableElements = this.accordionPanel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
    );

    focusableElements.forEach((element) => {
      // Store original tabindex if not already stored
      if (!this.originalTabIndexes.has(element)) {
        this.originalTabIndexes.set(element, element.getAttribute('tabindex'));
      }
      element.setAttribute('tabindex', '-1');
    });

    return this;
  }

  /**
   * Restores focus on all focusable elements inside the accordion panel
   * when the accordion is opened
   * @returns {object} A reference to the current instance of the class
   */
  enablePanelFocus() {
    this.originalTabIndexes.forEach((originalTabIndex, element) => {
      if (originalTabIndex === null) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabIndex);
      }
    });

    return this;
  }

  /**
   * Animates a panel collapse
   * @param {HTMLElement} node - REQUIRED - the accordion panel to be collapsed
   * @chainable
   */
  static collapseSection(node) {
    const element = node;
    const sectionHeight = element.scrollHeight;
    const elementTransition = element.style.transition;
    element.style.transition = '';

    requestAnimationFrame(() => {
      element.style.height = `${sectionHeight}px`;
      element.style.transition = elementTransition;
      requestAnimationFrame(() => {
        element.style.height = `${0}px`;
      });
    });
  }

  /**
   * Animates a panel expansion
   * @param {HTMLElement} node - REQUIRED - the accordion panel to be expanded
   * @param {HTMLElement} button - The accordion button element
   * @param {string} onlyExpand - Whether to only allow expansion
   * @chainable
   */
  static expandSection(node, button, onlyExpand) {
    const element = node;
    const sectionHeight = element.scrollHeight;
    element.style.height = `${sectionHeight}px`;

    element.addEventListener(EVENTS.TRANSITIONEND, () => {
      if (element.getAttribute(ARIA.HIDDEN) === MISC.FALSE) {
        element.style.height = 'auto';
      } else {
        element.style.height = '0px';
      }
      element.removeEventListener(EVENTS.TRANSITIONEND, () => {
        element.style.height = null;
      });
    });

    if (onlyExpand === MISC.TRUE) {
      /* eslint-disable no-param-reassign */
      button.disabled = true;
    }
  }
}
